import React from 'react';
import { useDispatch } from 'react-redux';

import SVG from 'components/base/SVG';
import Loader from 'components/Loader';
import InvestorProfile from 'app/InvestorOutreach/Profile';
import { getMapCardPhoto, showMapCardNoPhotoOnError } from 'app/InvestorOutreach/propertyPhoto';
import {
  getInvestorTypeBackground,
  getInvestorTypeColor,
  getInvestorTypeIcon,
  getInvestorTypeLabel,
  getPrimaryInvestorType,
} from 'app/InvestorOutreach/investorTypeTheme';
import { formatMailingAddress } from 'app/InvestorOutreach/Profile/format';
import {
  formatCompactCurrency,
  formatOwnership,
  formatProperties,
  formatPurchases,
} from 'data/investors/investorUtils';

import css from './style.scss';

// Right-hand card for an Investor Outreach lead in a dialer campaign (ATAI-520).
//
// Laid out like the compact property preview card a property lead gets
// (components/Map/PropertyInfo): the no-photo header with a Details button, a
// tag row, one headline figure, a teal address line and a metrics row — but
// filled from the investor's stored profile (GET /ps-core/investors/{id}),
// since an investor lead has no property of its own. Details opens the same
// Investor Profile modal Investor Outreach uses.
//
// `loading` is true until the profile request settles; `profile` is then null
// only when the investor has no stored snapshot (404), and the card falls back
// to the lead's own mailing address.
const formatLeadAddress = ({ streetAddress, city, state, zip } = {}) => {
  const region = [state, zip].filter(Boolean).join(' ');
  return [streetAddress, city, region].filter(Boolean).join(', ');
};

// The banner photo follows the Investor Outreach map card (ATAI-520 reopen): the
// investor's anchor property — their first linked property that can be placed
// on the map, as buildInvestorPins picks it — through the map card's own photo
// rule, so the same investor shows the same picture on both surfaces and only
// falls back to the no-photo image when there really is none.
const hasPosition = (property) => {
  const lat = Number(property && property.latitude);
  const lng = Number(property && property.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
};

const getAnchorProperty = (profile) => {
  const properties = (profile && profile.properties) || [];
  return properties.find((p) => p && p.propertyId && hasPosition(p)) || properties[0] || null;
};

export const TypeTag = ({ type }) => (
  <span
    className={css.tag}
    style={{ color: getInvestorTypeColor(type), backgroundColor: getInvestorTypeBackground(type) }}
  >
    <SVG icon={getInvestorTypeIcon(type)} className={css.tag__icon} />
    {getInvestorTypeLabel(type)}
  </span>
);

export const Metric = ({ label, value }) => (
  <div className={css.metric}>
    <span className={css.metric__label}>{label}</span>
    <span className={css.metric__value}>{value}</span>
  </div>
);

const InvestorLeadCard = ({ investorId, contactId, contactInfo, profile, loading }) => {
  const dispatch = useDispatch();
  const metrics = (profile && profile.metrics) || {};
  const type = getPrimaryInvestorType(profile && profile.classifications) || 'INVESTOR';
  const address =
    formatMailingAddress(((profile && profile.mailingAddresses) || [])[0]) ||
    formatLeadAddress(contactInfo);

  const openDetails = () =>
    dispatch(InvestorProfile.open({ investor: { id: investorId, contactId, saved: true } }));

  // ATAI-520 reopen: rendering before the profile arrived showed the generic
  // "Investor" tag and the lead's partial address, which then jumped to the
  // real type and full mailing address. Hold the whole card until it lands.
  if (loading) {
    return (
      <div className={`${css.card} ${css.cardLoading}`}>
        <Loader loaderSize="small" />
      </div>
    );
  }

  return (
    <div className={css.card}>
      <div className={css.image}>
        <img
          className={css.image__photo}
          src={getMapCardPhoto(getAnchorProperty(profile))}
          onError={showMapCardNoPhotoOnError}
          alt=""
        />
        <button type="button" className={css.details} onClick={openDetails}>
          Details
        </button>
      </div>

      <div className={css.body}>
        <div className={css.tags}>
          <TypeTag type={type} />
        </div>

        <div className={css.headline}>
          <span className={css.headline__value}>{formatProperties(metrics.portfolioSize)}</span>
          <span className={css.headline__label}>Portfolio Size</span>
        </div>

        <p className={css.address} title={address || undefined}>
          {address || 'Mailing address unavailable'}
        </p>

        <div className={css.metrics}>
          <Metric label="Last 12 Months" value={formatPurchases(metrics.acquisitionCountLast12Months)} />
          <Metric label="Avg Purchase Price" value={formatCompactCurrency(metrics.avgPurchasePrice)} />
          <Metric label="Ownership" value={formatOwnership(metrics.avgOwnershipLengthMonths)} />
          <Metric label="Portfolio Value" value={formatCompactCurrency(metrics.totalEstimatedValue)} />
        </div>
      </div>
    </div>
  );
};

export default InvestorLeadCard;
